import { createDragController } from '../controller/drag';
import { createGame } from '../controller/game';
import { BOARD_H, BOARD_W } from '../core/constants';
import { initialPlacement } from '../core/shuffle';
import { IDENTITY_TARGET } from '../core/stage';
import type { Placement, TargetLayout } from '../core/types';
import { createBoardView, cssImageValue } from '../view/board-view';
import { SECRET_FADE_MS, SECRET_SHAKE_MS } from '../view/constants';
import { createDragLayer } from '../view/drag-layer';
import { createClearEffects } from '../view/effects';
import { applyIntroStart, planIntro, runIntro } from '../view/intro';
import { observeLayout } from '../view/layout';
import { initAgeGate } from './age-gate';
import { isCrawler } from './crawler';
import { attachInterstitial } from './interstitial';
import { createProgressStore } from './progress';
import { PROMO } from './promo';
import { attachRuleHint } from './rule-hint';
import { SECRET_IMAGE_URL } from './secret-image';
import { initSeriesSelect } from './series-select';
import { SITE_NAME } from './site-name';
import { attachSkipLink, skipTarget } from './skip';
import { appendSocialToFooter, buildSocialLink } from './social';
import { readStageConfig } from './stage-config';
import { safeStorage } from './storage';
import './main.css';

/**
 * 「シリーズ選択へ戻りますか？」の確認モーダル（TK-057）。
 *
 * ゲーム中のヘッダのサイト名を誤タップしても盤面（途中経過は保存されない。FR-019）を
 * 失わないよう、直接遷移せず OK でだけトップへ戻す。OK は `/` への通常のリンク、
 * キャンセル・背景タップ・Esc でモーダルを閉じてゲームへ戻る。
 */
function openConfirmToTop(): void {
  const overlay = document.createElement('div');
  overlay.className = 'confirm-overlay';
  const dialog = document.createElement('div');
  dialog.className = 'confirm-dialog';
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  const message = document.createElement('p');
  message.className = 'confirm-message';
  message.id = 'confirm-to-top-message';
  message.textContent = 'シリーズ選択へ戻りますか？';
  dialog.setAttribute('aria-labelledby', message.id);
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'confirm-cancel';
  cancel.textContent = 'キャンセル';
  const ok = document.createElement('a');
  ok.className = 'confirm-ok';
  ok.href = '/';
  ok.textContent = 'OK';
  const onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') close();
  };
  const close = (): void => {
    overlay.remove();
    document.removeEventListener('keydown', onKey);
  };
  cancel.addEventListener('click', close);
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) close();
  });
  document.addEventListener('keydown', onKey);
  const actions = document.createElement('div');
  actions.className = 'confirm-actions';
  actions.append(cancel, ok);
  dialog.append(message, actions);
  overlay.append(dialog);
  document.body.append(overlay);
  cancel.focus();
}

/**
 * ページの骨格を組み立てる。
 *
 * ヘッダ・盤面領域・下部導線帯の 3 行構成にする。3 つがビューポート内に収まることは
 * CSS のグリッド行構成が保証し、盤面がその領域に収まることは `observeLayout` が
 * 倍率を調整して保証する（FR-004）。
 *
 * 盤面の中身は TK-010（盤面とピースの描画）、下部導線帯の中身は TK-018 で入る。
 *
 * 下部導線帯は 2 層を同じグリッドセルに重ねる: クリア後に現れる導線の行（`outbound`。
 * Next Stage・送客導線・X への導線）と、プレイ中だけ見える `Skip Stage`（`band` 直下）。
 * 両者は同時に見えることがないため場所を共有させ、帯の高さを導線の行だけで決める。
 * こうすると Skip Stage の有無やクリア時の取り除きで帯の高さ・盤面の倍率が動かない
 * （FR-004 / FR-015、TK-070）。
 */
function buildSkeleton(root: HTMLElement): {
  stage: HTMLElement;
  board: HTMLElement;
  banner: HTMLElement;
  band: HTMLElement;
  outbound: HTMLElement;
} {
  root.replaceChildren();

  const header = document.createElement('header');
  header.className = 'app-header';
  // サイト名はトップ（シリーズ選択）へのリンク（TK-057）。ゲーム中の誤タップで盤面を
  // 失うのは悲しいので、直接遷移せず確認モーダルを挟む。
  const title = document.createElement('a');
  title.className = 'app-title';
  title.href = '/';
  title.textContent = SITE_NAME;
  title.addEventListener('click', (event) => {
    event.preventDefault();
    openConfirmToTop();
  });
  // ルール説明への導線（TK-045）。全ステージのヘッダから飛べる。
  const rules = document.createElement('a');
  rules.className = 'rules-link';
  rules.href = '/rules.html';
  rules.textContent = '遊び方';
  header.append(title, rules);

  const stage = document.createElement('div');
  stage.className = 'board-stage';

  const board = document.createElement('div');
  board.className = 'board';
  stage.append(board);

  // クリア演出のバナー（TK-014、FR-014）。骨格として最初から置き、hidden で出し入れする。
  const banner = document.createElement('div');
  banner.className = 'clear-banner';
  banner.textContent = 'Clear !';
  banner.hidden = true;
  stage.append(banner);

  const band = document.createElement('footer');
  band.className = 'app-outbound';
  const outbound = document.createElement('div');
  outbound.className = 'app-outbound-links';
  band.append(outbound);

  root.append(header, stage, band);
  return { stage, board, banner, band, outbound };
}

/**
 * クリア後の導線（FR-015）。次ステージへの `Next Stage`、最終ステージでは選択画面へ
 * 戻るリンクを、下部導線帯に**不可視のまま**先に置く。visibility: hidden は場所を保つため、
 * CLEARED での可視化はレイアウトシフトを起こさない。
 */
function buildNextLink(
  outbound: HTMLElement,
  config: { readonly next?: string; readonly isLast: boolean },
): HTMLAnchorElement | null {
  const href = config.isLast ? '/' : config.next;
  if (href === undefined) return null;
  const link = document.createElement('a');
  link.className = 'next-stage';
  link.href = href;
  link.textContent = config.isLast ? 'シリーズ選択へ' : 'Next Stage';
  outbound.append(link);
  return link;
}

/**
 * クリア後の送客導線（TK-018、FR-018）。`Next Stage` の隣に、姉妹サイトへの外部リンクを
 * 不可視のまま先に置く。URL・ラベル・表示有無は設定（`promo.config.json`）で差し替える。
 */
function buildOutboundLink(outbound: HTMLElement): HTMLAnchorElement | null {
  if (!PROMO.outbound.enabled) return null;
  const link = document.createElement('a');
  link.className = 'outbound-link';
  link.href = PROMO.outbound.url;
  link.target = '_blank';
  link.rel = 'noopener';
  link.textContent = PROMO.outbound.label;
  outbound.append(link);
  return link;
}

const root = document.querySelector<HTMLElement>('#app');
// クローラーには年齢認証も盤面の組み立ても適用しない（TK-069、FR-002）。骨格の組み立て
// （`buildSkeleton` の `replaceChildren`）は静的 HTML に置いた完成画像と `Next Stage` を
// 破棄するため、素通りさせるだけでは本文が空になる。**何も実行しない**ことで、
// クローラーは JS を無効にした訪問者とまったく同じもの — ページ生成が書き出した
// 静的フォールバック — を受け取る。出し分けはこの 1 か所だけで、エッジ・S3 側には
// 持たせない（ADR-003）。
if (root && !isCrawler(navigator.userAgent)) {
  // 盤面の組み立てとゲーム状態機械（TK-013）。初期配置はシードから毎回同じものが
  // 決まるので、リロードすると同じ盤面に戻る（FR-019）。埋め込み設定が無いページ
  // （404 等）では静的コンテンツをそのまま残し、骨格の組み立てを行わない（TK-022）。
  const config = readStageConfig();
  if (config !== null) {
    // 論理寸法の唯一の出所は core。CSS 側に数値を二重に書かない。
    root.style.setProperty('--board-w', `${BOARD_W}px`);
    root.style.setProperty('--board-h', `${BOARD_H}px`);

    const { stage, board, banner, band, outbound } = buildSkeleton(root);
    const layout = observeLayout(stage, board);

    const { cols: C, rows: R, secretLayout: tau } = config;
    const placement0 = initialPlacement(config.seed, C, R, tau);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // 開始演出の計画（TK-037）。シードから決定的で、リロードしても同じ演出になる。
    const intro = planIntro(config.seed, C, R, placement0);
    // 進捗の記録先。localStorage 不可ならメモリフォールバック（TK-033、`05` §4.2）。
    const progress = createProgressStore(safeStorage());

    // クリア演出（TK-014）。導線は骨格として先に確保し、CLEARED で可視化する。
    const nextLink = buildNextLink(outbound, config);
    // クリア済みステージの早送り（TK-055、FR-026）。未クリア・末尾・進捗なしでは出ない。
    // クリア後は Next Stage と行き先が重複するため、CLEARED で取り除く。
    const skipHref = skipTarget(config, progress.read());
    const skipLink = skipHref !== null ? attachSkipLink(band, skipHref) : null;
    const effects = createClearEffects({
      board,
      banner,
      next: nextLink,
      outbound: buildOutboundLink(outbound),
      // 運営元の X アカウントへの導線（TK-070）。クリア後 = 必ず年齢認証の後にだけ見える。
      social: buildSocialLink(outbound, PROMO.social),
      reducedMotion,
    });
    // ステージ遷移時の自社広告（TK-018、FR-022）。導線はクリア演出後にしか現れないため、
    // プレイ中の盤面に広告が載ることはない。
    if (nextLink !== null) attachInterstitial(nextLink, PROMO.interstitial);
    // 操作保護（TK-035）: 長押しのコンテキストメニュー（画像の保存・共有等）を盤面上で
    // 抑止する。選択・コールアウトの抑止は CSS 側（board.css）が受け持つ。
    board.addEventListener('contextmenu', (event) => event.preventDefault());

    const view = createBoardView(board, {
      C,
      R,
      image: cssImageValue(config.image),
      // ピース別タイル（TK-035）。プレイ中の DOM に原画像を現れさせない。
      ...(config.tiles !== undefined
        ? {
            tileImage: (piece: number) =>
              cssImageValue(`${config.tiles}.t${String(piece).padStart(2, '0')}.webp`),
          }
        : {}),
      // ステージ固有の秘文字レイヤー（TK-038）。無いステージは共通の仮素材で後退。
      secret: cssImageValue(config.secret ?? SECRET_IMAGE_URL),
      secretExit: intro.exit,
      secretLayout: tau,
      // 成人向けの内容であるため、絵の中身には踏み込まずに盤面であることだけを伝える
      // （NFR-006「コンテンツの性質に配慮した表現とする」）。
      label: 'ジグソーパズルの盤面',
      // QR 刻印の隅（TK-058）。Pipeline D が画像から決める。無い（開発ページ）なら右下。
      ...(config.qrCorner !== undefined ? { qrCorner: config.qrCorner } : {}),
    });

    // ルール誤解の検知（TK-041、FR-025）: 第 1 段階で絵柄側の仮想まとまりが育ったら促す。
    const ruleHint = attachRuleHint(stage, C);

    const game = createGame({
      C,
      R,
      tau,
      initialPlacement: placement0,
      render: (state, options) => {
        view.render(state, options);
        ruleHint.check(state.placement, state.phase);
      },
      setSecretVisible: view.setSecretVisible,
      // 段階遷移の入力停止時間 = 振動（TK-049）+ 退場フェード（TK-037）。
      secretFadeMs: SECRET_SHAKE_MS + SECRET_FADE_MS,
      runClearEffects: () => effects.run(),
      // 状態は data 属性に映す。CSS のフックと E2E の観測点を兼ねる。
      onStateChange: (state) => {
        board.dataset.gameState = state;
        // 演出完了で Next Stage（または選択画面への導線）をフェードインする（FR-015）。
        // Skip Stage は行き先が Next Stage と同じになるため、ここで取り除く（TK-055）。
        if (state === 'CLEARED') {
          effects.showNext();
          skipLink?.remove();
        }
        // クリアの確定（= 画像成立）で進捗を記録する（TK-033、FR-019）。演出の完了を
        // 待たない — 演出中にページを閉じてもクリアは成立している。
        if (state === 'CLEARING') {
          // 完成表示レイヤーへ原画像を書き込む（TK-036）。表示は board--cleared の CSS が行う。
          view.revealComplete();
          progress.recordClear(
            {
              series: config.series,
              stageNo: config.stageNo,
              stageCount: config.stageCount,
              ...(config.next !== undefined ? { next: config.next } : {}),
              isLast: config.isLast,
            },
            Date.now(),
          );
        }
      },
    });
    board.dataset.gameState = game.state();

    // 開始演出（TK-037）: 認証が済むまでピースは画面外（地だけが見える）。演出完了まで
    // ドラッグ入力を受け付けない。
    let introDone = false;
    if (!reducedMotion) applyIntroStart(intro, view.pieceElement);

    createDragController({
      board,
      pieceElement: view.pieceElement,
      scale: () => layout.scale(),
      state: () => game.board(),
      layer: createDragLayer(board, C, R),
      enabled: () => introDone && game.dragEnabled(),
      onDrop: (next: Placement, displaced, dragged) => game.commitDrop(next, displaced, dragged),
    });

    // 盤面を現在段階の目標配置へ直接遷移させる E2E 専用フック（TK-019、07-test-plan.md
    // 「E-8 / E-9 はテスト用フックを介して到達させる」）。本番ビルドでは `__DEV__` の
    // 畳み込みで消える（scripts/check-dev-stripped.mjs が出力側から検査する）。
    if (__DEV__) {
      const solvedPlacement = (target: TargetLayout): Placement => {
        const cells: number[] = new Array(target.length);
        for (const [piece, cell] of target.entries()) cells[cell] = piece;
        return cells as unknown as Placement;
      };
      (window as Window & { __zpTestSolve?: (which?: 'image') => void }).__zpTestSolve = (
        which,
      ) => {
        // 既定は現在段階の目標。'image' は段階に関係なく完成画像の配置へ遷移させる
        // （第 1 段階でのルール誤解ヒント（TK-041）の検証に使う。τ は揃わないため
        // 段階は進まない）。
        const target =
          which !== 'image' && board.dataset.gameState === 'PLAYING_SECRET'
            ? tau
            : IDENTITY_TARGET(C * R);
        game.commitDrop(solvedPlacement(target));
      };
    }

    // 認証済みなら即座に、そうでなければ「18歳以上」の選択で AGE_GATE から先へ進む。
    // オーバーレイは root の外（body 直下）に挿入されるため、root の inert に巻き込まれない。
    // 認証の成立でフライイン（TK-037）を再生し、完了で入力を解禁する。
    initAgeGate(root, () => {
      game.start();
      void runIntro(intro, board, view.pieceElement, { reducedMotion }).then(() => {
        introDone = true;
      });
    });
  } else {
    // シリーズ選択画面（TK-032、FR-023）: 静的一覧へ New・クリア・続きからの装飾を載せる。
    // 進捗が読めない・壊れている場合は空の進捗が返り、素の一覧のまま機能する。
    if (root.querySelector('.series-list') !== null) {
      initSeriesSelect(root, createProgressStore(safeStorage()).read(), Date.now());
    }
    // 盤面を持たないページ（404 等）でも認証は適用する。運営元の X アカウントへの導線
    // （TK-070）は R18 アカウントへのリンクなので、静的 HTML には書かず認証の成立後に足す。
    initAgeGate(root, () => appendSocialToFooter(root, PROMO.social));
  }
}
